import React, { SyntheticEvent, useCallback, useContext, useEffect } from 'react';
import { usePrevious } from 'hooks';
import isEqual from 'lodash/isEqual';
import { EMPTY_BUILDER_ITEMS } from 'components/MealDealPage/constants';
import { DealBuilderContext } from 'components/Builder/context';
import { BaseGroupItemPlaceholder } from 'components/Builder/types';
import { useWtrSelector } from 'redux/hooks';
import { getBuilderGroupByIndex } from 'components/Builder/selectors';
import LineView from '../LineView';
import { DELAY_CLOSE_ON_ADD } from './constants';

export interface BuilderMobileLineProps {
  builderIndex: number;
  collapsed: boolean;
  groupId: string;
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  groupIndex: number;
  name?: string;
  promotionId?: string;
  builderId?: string;
  variant: 'bundle' | 'meal deal' | 'offer' | 'wine';
}

const BuilderMobileLine = ({
  builderIndex,
  collapsed,
  groupId,
  GroupItemPlaceholder,
  groupIndex,
  name,
  promotionId,
  builderId,
  variant,
}: BuilderMobileLineProps) => {
  const { items = EMPTY_BUILDER_ITEMS, threshold } = useWtrSelector(
    state =>
      getBuilderGroupByIndex(state, { promotionId, index: builderIndex, variant, groupIndex }),
    isEqual,
  );
  const completed = items.filter((item: unknown) => !!item).length >= threshold;
  const {
    dismissProductsScreen,
    setModalDismissFocusTargetId,
    setSelectedGroup,
    setProductCountAndTotal,
  } = useContext(DealBuilderContext);

  const handleSlotClick = useCallback(
    (event: SyntheticEvent<HTMLElement>) => {
      setSelectedGroup(groupId);
      const id = event.currentTarget.getAttribute('data-lineitem');
      setModalDismissFocusTargetId(id || '');
    },
    [groupId, setModalDismissFocusTargetId, setSelectedGroup],
  );

  const prevCompleted = usePrevious(completed);
  const justCompleted = completed && completed !== prevCompleted;

  const productCount = items.filter((i: unknown) => i !== null).length;
  const prevProductCount = usePrevious(productCount);
  const initialised = prevProductCount !== undefined;

  const productAdded = initialised && productCount > 0 && productCount > prevProductCount;
  const productRemoved = initialised && productCount < prevProductCount;

  useEffect(() => {
    if (productAdded && setProductCountAndTotal) {
      setProductCountAndTotal({ totalCount: items.length, productCount });
    }
  }, [productAdded, setProductCountAndTotal, items.length, productCount]);

  useEffect(() => {
    if (productRemoved && setProductCountAndTotal) {
      setProductCountAndTotal(null);
    }
  }, [productRemoved, setProductCountAndTotal]);

  useEffect(
    () => () => {
      if (setProductCountAndTotal) {
        setProductCountAndTotal(null);
      }
    },
    [setProductCountAndTotal],
  );

  useEffect(() => {
    if (initialised && justCompleted) {
      setTimeout(dismissProductsScreen, DELAY_CLOSE_ON_ADD);
    }
  }, [initialised, justCompleted, dismissProductsScreen]);

  return (
    <LineView
      builderIndex={builderIndex}
      collapsed={collapsed}
      GroupItemPlaceholder={GroupItemPlaceholder}
      isMobile
      groupIndex={groupIndex}
      name={name}
      onSlotClick={handleSlotClick}
      promotionId={promotionId}
      variant={variant}
      groupId={groupId}
      builderId={builderId}
    />
  );
};

BuilderMobileLine.displayName = 'BuilderMobileLine';

export default BuilderMobileLine;
